import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Spinner, Alert, 
  Button, Modal, ListGroup, Badge 
} from 'react-bootstrap';
import { publicDepartmentsAPI } from '../../services/publicApi';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await publicDepartmentsAPI.getAll();
      
      if (result && result.success) {
        setDepartments(result.data || []);
      } else {
        setError(result?.message || 'Failed to load departments');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (department) => {
    setSelectedDepartment(department);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Hero Section */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-primary mb-3">Our Departments</h1>
          <p className="lead text-muted mb-4" style={{ maxWidth: '800px', margin: '0 auto' }}>
            Explore our diverse range of academic departments offering quality education, 
            experienced faculty, and excellent infrastructure
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Badge bg="primary" className="p-2">Undergraduate Programs</Badge>
            <Badge bg="success" className="p-2">Postgraduate Programs</Badge>
            <Badge bg="warning" className="p-2">Research Facilities</Badge>
            <Badge bg="info" className="p-2">Industry Collaborations</Badge>
          </div>
        </div>

        {error && (
          <Alert variant="warning" className="mb-4" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 fs-5">Loading departments...</p>
          </div>
        ) : departments.length > 0 ? (
          <>
            <div className="mb-4 text-center">
              <h5 className="text-muted">
                <i className="fas fa-building me-2"></i>
                {departments.length} Departments Available
              </h5>
            </div>
            
            <Row className="g-4">
              {departments.map((dept) => (
                <Col key={dept.id} lg={4} md={6}>
                  <Card className="h-100 shadow border-0 hover-lift">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-start mb-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                          <i className="fas fa-graduation-cap fa-2x text-primary"></i>
                        </div>
                        <div>
                          <Card.Title className="text-primary mb-1">
                            {dept.name}
                          </Card.Title>
                          {dept.code && (
                            <Badge bg="info" className="mb-2">
                              {dept.code}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {dept.description && (
                        <Card.Text className="text-muted mb-3">
                          {dept.description.length > 100 
                            ? `${dept.description.substring(0, 100)}...`
                            : dept.description}
                        </Card.Text>
                      )}
                      
                      <div className="department-info mb-3">
                        {dept.hod_name && (
                          <div className="mb-1">
                            <small className="text-muted">
                              <i className="fas fa-user-tie me-1"></i>
                              <strong>HOD:</strong> {dept.hod_name}
                            </small>
                          </div>
                        )}
                        
                        {dept.building && (
                          <div className="mb-1">
                            <small className="text-muted">
                              <i className="fas fa-building me-1"></i>
                              <strong>Location:</strong> {dept.building}
                              {dept.floor && `, Floor ${dept.floor}`}
                            </small>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4">
                        <Button 
                          variant="primary" 
                          className="w-100"
                          onClick={() => handleViewDetails(dept)}
                        >
                          <i className="fas fa-eye me-2"></i>
                          View Details
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mb-4">
              <i className="fas fa-university fa-4x text-muted opacity-50"></i>
            </div>
            <h3 className="fw-bold mb-3">No Departments Found</h3>
            <p className="text-muted mb-4">
              Department information will be available soon. Please check back later.
            </p>
            <Button variant="primary" onClick={fetchDepartments}>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </Button>
          </div>
        )}
      </Container>

      {/* Department Details Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        {selectedDepartment && (
          <>
            <Modal.Header closeButton className="bg-primary text-white">
              <Modal.Title>
                <i className="fas fa-graduation-cap me-2"></i>
                {selectedDepartment.name} Department
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <Row>
                <Col lg={8}>
                  <div className="mb-4">
                    <h5 className="fw-bold text-primary mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      About the Department
                    </h5>
                    {selectedDepartment.description && (
                      <div className="mb-3">
                        {selectedDepartment.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-2">{paragraph}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="fw-bold text-primary mb-3">
                      <i className="fas fa-graduation-cap me-2"></i>
                      Academic Programs
                    </h5>
                    <Row>
                      <Col md={6} className="mb-2">
                        <Card className="border-0 bg-light">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <div className="bg-primary text-white rounded-circle p-2 me-3">
                                <i className="fas fa-user-graduate"></i>
                              </div>
                              <div>
                                <h6 className="fw-bold mb-1">Undergraduate</h6>
                                <small className="text-muted">Bachelor's Degree Programs</small>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6} className="mb-2">
                        <Card className="border-0 bg-light">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <div className="bg-success text-white rounded-circle p-2 me-3">
                                <i className="fas fa-user-graduate"></i>
                              </div>
                              <div>
                                <h6 className="fw-bold mb-1">Postgraduate</h6>
                                <small className="text-muted">Master's Degree Programs</small>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Col>
                
                <Col lg={4}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-light">
                      <h6 className="fw-bold mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Department Information
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {selectedDepartment.code && (
                          <ListGroup.Item className="d-flex align-items-center py-2">
                            <i className="fas fa-hashtag text-primary me-3"></i>
                            <div>
                              <small className="text-muted">Department Code</small>
                              <div className="fw-bold">{selectedDepartment.code}</div>
                            </div>
                          </ListGroup.Item>
                        )}
                        
                        {selectedDepartment.hod_name && (
                          <ListGroup.Item className="d-flex align-items-center py-2">
                            <i className="fas fa-user-tie text-primary me-3"></i>
                            <div>
                              <small className="text-muted">Head of Department</small>
                              <div className="fw-bold">{selectedDepartment.hod_name}</div>
                              {selectedDepartment.hod_email && (
                                <small className="text-muted">{selectedDepartment.hod_email}</small>
                              )}
                            </div>
                          </ListGroup.Item>
                        )}
                        
                        {selectedDepartment.building && (
                          <ListGroup.Item className="d-flex align-items-center py-2">
                            <i className="fas fa-building text-primary me-3"></i>
                            <div>
                              <small className="text-muted">Location</small>
                              <div className="fw-bold">{selectedDepartment.building}</div>
                              {selectedDepartment.floor && (
                                <small className="text-muted">Floor: {selectedDepartment.floor}</small>
                              )}
                            </div>
                          </ListGroup.Item>
                        )}
                        
                        {selectedDepartment.hod_contact && (
                          <ListGroup.Item className="d-flex align-items-center py-2">
                            <i className="fas fa-phone text-primary me-3"></i>
                            <div>
                              <small className="text-muted">Contact</small>
                              <div className="fw-bold">{selectedDepartment.hod_contact}</div>
                            </div>
                          </ListGroup.Item>
                        )}
                        
                        <ListGroup.Item className="d-flex align-items-center py-2">
                          <i className="fas fa-calendar-alt text-primary me-3"></i>
                          <div>
                            <small className="text-muted">Established</small>
                            <div className="fw-bold">
                              {formatDate(selectedDepartment.created_at)}
                            </div>
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                  
                  <div className="mt-3">
                    <Button variant="outline-primary" className="w-100 mb-2">
                      <i className="fas fa-download me-2"></i>
                      Download Brochure
                    </Button>
                    <Button variant="primary" className="w-100">
                      <i className="fas fa-user-graduate me-2"></i>
                      Apply for Admission
                    </Button>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>
              <Button variant="primary" as="a" href="/contact">
                <i className="fas fa-envelope me-2"></i>
                Contact Department
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <style jsx="true">{`
        /* Fix for header overlap */
        body {
          padding-top: 0px;
        }
        
        /* Card hover effect */
        .hover-lift {
          transition: all 0.3s ease;
          border: 1px solid #e9ecef;
        }
        
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
          border-color: #0d6efd;
        }
        
        /* Department info styling */
        .department-info {
          background-color: #f8f9fa;
          padding: 10px;
          border-radius: 8px;
          border-left: 4px solid #0d6efd;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          h1.display-4 {
            font-size: 2.5rem;
          }
          
          .modal-body {
            padding: 1rem;
          }
        }
        
        @media (max-width: 576px) {
          h1.display-4 {
            font-size: 2rem;
          }
          
          .card {
            margin-bottom: 1rem;
          }
        }
        
        /* Modal custom styles */
        .modal-content {
          border-radius: 15px;
          overflow: hidden;
        }
        
        .modal-header {
          border-bottom: 3px solid rgba(255, 255, 255, 0.2);
        }
        
        /* List group item hover */
        .list-group-item {
          border: none;
          border-bottom: 1px solid #e9ecef;
          transition: background-color 0.2s;
        }
        
        .list-group-item:hover {
          background-color: #f8f9fa;
        }
        
        .list-group-item:last-child {
          border-bottom: none;
        }
      `}</style>
    </div>
  );
};

export default Departments;